import { useState, useEffect, useRef, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { axiosInstance } from "../../config";
import { AuthContext } from "../../context/AuthContext";
import Topbar from "../../components/topbar/Topbar";
import Leftbar from "../../components/LeftBar/lefbar";
import Avatar from "../../components/Avatar/Avatar";
import UpgradeModal from "../../components/UpgradeModal/UpgradeModal";
import { openTrialCheckout } from "../../utils/trialCheckout";
import { FiArrowLeft, FiCamera, FiPlay, FiUser } from "react-icons/fi";
import "../../components/Onboarding/onboarding.css";
import { useFileDrop } from "../../utils/useFileDrop";
import toast from "react-hot-toast";
import { HOOK_COLORS, HOOK_FONTS } from "../../utils/captionConstants";
import { SMS_CONSENT_LANGUAGE } from "../../utils/smsConsent";
import DeveloperApiSection from "../../components/DeveloperApiSection/DeveloperApiSection";
import "./mepage.css";

const ADD_STREAMER_RETURN_KEY = "cb_add_streamer_return"; // set by the campaign launch flow

const PF = process.env.REACT_APP_PUBLIC_FOLDER || '/assets/';

const PLATFORMS = [
  { value: "", labelKey: "selectPlatform" },
  { value: "twitch", labelKey: "platformTwitch" },
  { value: "youtube", labelKey: "platformYouTube" },
  { value: "kick", labelKey: "platformKick" },
  { value: "rumble", labelKey: "platformRumble" },
  { value: "x", labelKey: "platformXTwitter" },
  { value: "ganjing", labelKey: "platformGanjing" },
  { value: "own", labelKey: "platformOwn" },
];

const PLATFORM_ICON = {
  twitch: "/twitch.png", youtube: "/youtube.png", kick: "/kick.png", rumble: "/rumble.png", x: "/x.png", ganjing: "/ganjing.png",
};

const STREAM_TYPES = [
  { value: "gaming",       labelKey: "streamTypeGaming",      descKey: "streamTypeGamingDesc" },
  { value: "talking_head", labelKey: "streamTypeTalkingHead", descKey: "streamTypeTalkingHeadDesc" },
  { value: "irl",          labelKey: "streamTypeIRL",         descKey: "streamTypeIRLDesc" },
  { value: "music",        labelKey: "streamTypeMusic",       descKey: "streamTypeMusicDesc" },
  { value: "documentary",  labelKey: "streamTypeDocumentary", descKey: "streamTypeDocumentaryDesc" },
];

const STREAM_TYPE_ICONS = {
  gaming: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="6" width="20" height="12" rx="4" />
      <line x1="6" y1="10" x2="10" y2="10" /><line x1="8" y1="8" x2="8" y2="12" />
      <circle cx="15" cy="9" r="1" fill="currentColor" stroke="none" />
      <circle cx="18" cy="11" r="1" fill="currentColor" stroke="none" />
    </svg>
  ),
  talking_head: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
      <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
      <line x1="12" y1="19" x2="12" y2="22" />
    </svg>
  ),
  irl: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="2" y1="12" x2="22" y2="12" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
  ),
  music: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 18V5l12-2v13" />
      <circle cx="6" cy="18" r="3" />
      <circle cx="18" cy="16" r="3" />
    </svg>
  ),
  documentary: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="3" width="20" height="14" rx="2" />
      <path d="M8 21h8M12 17v4" />
      <circle cx="12" cy="10" r="3" />
      <path d="M12 7v1M12 12v1M9 10h1M14 10h1" />
    </svg>
  ),
};

const SOCIAL_PLATFORMS = [
  { key: "x", nameKey: "socialXTwitter", icon: "𝕏", color: "#000" },
  // YouTube Shorts publishing — enabled so the OAuth flow can be demonstrated
  // for Google verification (works for the developer + test users pre-approval).
  { key: "youtube", nameKey: "socialYouTubeShorts", icon: <FiPlay />, color: "#FF0000" },
  // TikTok: admin-only until TikTok approves the app (see ADMIN_ONLY_PLATFORMS).
  // Temporarily hidden until platform approvals are complete:
  // { key: "facebook", nameKey: "socialFacebookReels", icon: "f", color: "#1877F2", comingSoon: true },
  // { key: "rumble", nameKey: "socialRumbleShorts", icon: "R", color: "#85c742", comingSoon: true },
  // { key: "ganjing", nameKey: "socialGanjingWorld", iconImg: "/ganjing.png", color: "#fff", comingSoon: true },
];

// Shown only to admins while the platform's app review is pending.
const ADMIN_ONLY_PLATFORMS = [
  { key: "tiktok", nameKey: "socialTikTok", icon: "♪", color: "#000" },
];

export default function MePage() {
  const { t } = useTranslation(['settings', 'common']);
  const navigate = useNavigate();
  const location = useLocation();
  const { user: currentUser, dispatch } = useContext(AuthContext);
  const fileInputRef = useRef(null);

  const [channelUsername, setChannelUsername] = useState("");
  const [platform, setPlatform] = useState("");
  const [streamerName, setStreamerName] = useState("");
  const [streamType, setStreamType] = useState("");
  const [socialConnections, setSocialConnections] = useState({});
  const [isAdmin, setIsAdmin] = useState(false);
  const [saving, setSaving] = useState(false);
  const [profilePicture, setProfilePicture] = useState(null);
  const [uploadingPicture, setUploadingPicture] = useState(false);

  // Brand state
  const [brands, setBrands] = useState([]);
  const [activeBrandId, setActiveBrandId] = useState(null);
  const [showNewBrand, setShowNewBrand] = useState(false);
  // Plan / credits gate for premium features (Add Brand, etc.).
  const [mePlan, setMePlan] = useState(null); // null = loading
  const [meCredits, setMeCredits] = useState(0);
  const [meHasSub, setMeHasSub] = useState(false); // active/trialing Stripe subscription
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [trialOpening, setTrialOpening] = useState(false);
  const startTrial = async () => {
    if (trialOpening) return;
    setTrialOpening(true);
    try {
      await openTrialCheckout({ source: "me_banner" }); // leaving for Stripe
    } catch (err) {
      toast.error(err.response?.data?.message || "Couldn't open checkout. Please try again.");
      setTrialOpening(false);
    }
  };
  const [newBrandName, setNewBrandName] = useState("");
  const [creatingBrand, setCreatingBrand] = useState(false);

  // New brand wizard state
  const [wizardStep, setWizardStep] = useState(0); // 0=name,1=photo,2=platform,3=username,4=contentType,5=done
  const [wizardBrandId, setWizardBrandId] = useState(null);
  const [wizardPlatform, setWizardPlatform] = useState("");
  const [wizardUsername, setWizardUsername] = useState("");
  const [wizardContentType, setWizardContentType] = useState("");

  // Caption preset state
  const [captionPresets, setCaptionPresets] = useState([]);
  const [activePresetId, setActivePresetId] = useState(null);
  const [showPresetEditor, setShowPresetEditor] = useState(false);
  const [editingPreset, setEditingPreset] = useState(null);
  const [presetForm, setPresetForm] = useState({
    name: "", hookFont: "montserrat", hookColor: "white", hookY: 33, hookWidth: 92,
  });
  const [savingPreset, setSavingPreset] = useState(false);

  // Account settings state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteInput, setDeleteInput] = useState("");
  const [deleting, setDeleting] = useState(false);

  // SMS preferences state — `smsOptedOut === null` means we haven't loaded yet,
  // so the toggle stays neutral instead of flickering on/off during fetch.
  const [smsPhoneNumber, setSmsPhoneNumber] = useState(null);
  const [smsOptedOut, setSmsOptedOut] = useState(null);
  const [smsUpdating, setSmsUpdating] = useState(false);
  // Inline phone editor — adding a number for the first time OR changing the
  // saved one. Every save re-captures explicit, unchecked-by-default consent
  // (TCPA / A2P 10DLC), so we reset both the input and the checkbox each
  // time the editor opens.
  const [smsEditing, setSmsEditing] = useState(false);
  const [smsPhoneInput, setSmsPhoneInput] = useState("");
  const [smsConsent, setSmsConsent] = useState(false);
  const [smsSaving, setSmsSaving] = useState(false);
  const [showDeleteBrandConfirm, setShowDeleteBrandConfirm] = useState(false);
  const [deletingBrand, setDeletingBrand] = useState(false);
  const [showBrandMenu, setShowBrandMenu] = useState(false);
  const brandMenuRef = useRef(null);

  const fetchCreatorSettings = async () => {
    try {
      const res = await axiosInstance.get("/users/me/creator-settings");
      if (res.data) {
        setChannelUsername(res.data.creatorSettings?.channelUsername || "");
        setPlatform(res.data.creatorSettings?.platform || "");
        setStreamerName(res.data.streamerName || "");
        setStreamType(res.data.creatorSettings?.streamType || "");
        setSocialConnections(res.data.socialConnections || {});
        setIsAdmin(!!res.data.isAdmin);
        setProfilePicture(res.data.profilePicture || null);
      }
    } catch (err) {
      console.error("Failed to fetch settings:", err);
    }
  };

  const fetchBrands = async () => {
    try {
      const res = await axiosInstance.get("/users/me/brands");
      setBrands(res.data.brands || []);
      setActiveBrandId(res.data.activeBrandId || null);
    } catch (err) {
      console.error("Failed to fetch brands:", err);
    }
  };

  const fetchCaptionPresets = async () => {
    try {
      const res = await axiosInstance.get("/users/me/caption-presets");
      setCaptionPresets(res.data.presets || []);
      setActivePresetId(res.data.activePresetId || null);
    } catch (err) {
      console.error("Failed to fetch caption presets:", err);
    }
  };

  const handleSwitchPreset = async (presetId) => {
    if (presetId === activePresetId) return;
    try {
      await axiosInstance.put(`/users/me/caption-presets/${presetId}/activate`);
      setActivePresetId(presetId);
      toast.success(t('presetActivated'));
    } catch (err) {
      toast.error(t('failedToSwitchPreset'));
    }
  };

  const handleSavePreset = async () => {
    if (!presetForm.name.trim()) return;
    setSavingPreset(true);
    try {
      if (editingPreset) {
        await axiosInstance.put(`/users/me/caption-presets/${editingPreset._id}`, presetForm);
        toast.success(t('common:saved'));
      } else {
        await axiosInstance.post("/users/me/caption-presets", presetForm);
        toast.success(t('presetCreated'));
      }
      setShowPresetEditor(false);
      setEditingPreset(null);
      fetchCaptionPresets();
    } catch (err) {
      toast.error(err.response?.data?.error || t('common:failedToSave'));
    } finally {
      setSavingPreset(false);
    }
  };

  const handleDeletePreset = async (presetId) => {
    if (captionPresets.length <= 1) {
      toast.error(t('cannotDeleteLastPreset'));
      return;
    }
    try {
      const res = await axiosInstance.delete(`/users/me/caption-presets/${presetId}`);
      setActivePresetId(res.data.activePresetId);
      toast.success(t('presetDeleted'));
      fetchCaptionPresets();
    } catch (err) {
      toast.error(err.response?.data?.error || t('failedToDeletePreset'));
    }
  };

  const handleEditPreset = (preset) => {
    setEditingPreset(preset);
    setPresetForm({
      name: preset.name,
      hookFont: preset.hookFont,
      hookColor: preset.hookColor,
      hookY: preset.hookY,
      hookWidth: preset.hookWidth,
    });
    setShowPresetEditor(true);
  };

  const handleNewPreset = () => {
    setEditingPreset(null);
    setPresetForm({ name: "", hookFont: "montserrat", hookColor: "white", hookY: 33, hookWidth: 92 });
    setShowPresetEditor(true);
  };

  const handlePictureClick = () => {
    fileInputRef.current?.click();
  };

  // Add Streamer's photo step: drop a photo anywhere on the screen (desktop).
  const draggingPhoto = useFileDrop(
    (file) => handlePictureChange({ target: { files: [file] } }, wizardBrandId),
    showNewBrand && wizardStep === 1 && !uploadingPicture
  );

  const handlePictureChange = async (e, brandIdOverride) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error(t('pleaseSelectImageFile'));
      return;
    }

    if (file.size > 20 * 1024 * 1024) {
      toast.error("Image must be under 20MB");
      return;
    }

    setUploadingPicture(true);
    const formData = new FormData();

    const effectiveBrandId = brandIdOverride ?? activeBrandId;

    try {
      let newUrl;
      if (effectiveBrandId) {
        // Upload to brand avatar
        formData.append("avatar", file);
        const res = await axiosInstance.post(`/users/me/brands/${effectiveBrandId}/profile-picture`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        newUrl = res.data.avatar;
        setBrands(prev => prev.map(b =>
          b._id === effectiveBrandId ? { ...b, avatar: newUrl } : b
        ));
      } else {
        // Fallback: upload to global profile picture
        formData.append("profilePicture", file);
        const res = await axiosInstance.post("/users/me/profile-picture", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        newUrl = res.data.profilePicture;
      }
      setProfilePicture(newUrl);
      window.dispatchEvent(new CustomEvent("profile_picture_updated", {
        detail: { profilePicture: newUrl }
      }));
      toast.success(t('profilePictureUpdated'));
    } catch (err) {
      toast.error(t('failedToUploadPicture'));
    } finally {
      setUploadingPicture(false);
    }
  };

  const getProfilePictureUrl = () => {
    if (!profilePicture) return null;
    if (profilePicture.startsWith("http")) return profilePicture;
    return PF + profilePicture;
  };

  const fetchSmsPreferences = async () => {
    try {
      const res = await axiosInstance.get("/billing/plan/status");
      setSmsPhoneNumber(res.data?.phoneNumber ?? null);
      setSmsOptedOut(!!res.data?.smsOptedOut);
      setMePlan(res.data?.plan || "none");
      setMeCredits(typeof res.data?.credits === "number" ? res.data.credits : 0);
      // A trial subscriber can still have plan "none" until the first paid
      // invoice — treat an existing Stripe subscription as "already on a plan"
      // so the $1 trial banner doesn't show to someone mid-trial.
      setMeHasSub(!!res.data?.stripeSubscriptionId);
    } catch (err) {
      console.error("Failed to fetch SMS preferences:", err);
    }
  };

  const handleToggleSmsNotifications = async () => {
    if (smsUpdating || smsOptedOut === null) return;
    const next = !smsOptedOut;
    setSmsUpdating(true);
    setSmsOptedOut(next);
    try {
      await axiosInstance.patch("/sms/preferences", { smsOptedOut: next });
      toast.success(next ? "SMS notifications turned off" : "SMS notifications turned on");
    } catch (err) {
      console.error("Failed to update SMS preferences:", err);
      setSmsOptedOut(!next);
      toast.error("Couldn't update SMS preferences. Try again.");
    } finally {
      setSmsUpdating(false);
    }
  };

  const openSmsEditor = () => {
    // Pre-fill with the current number so it's obvious what they're editing,
    // but leave the checkbox UNCHECKED — re-consent is required for the save
    // even when only the number is changing.
    setSmsPhoneInput(smsPhoneNumber || "");
    setSmsConsent(false);
    setSmsEditing(true);
  };

  const cancelSmsEditor = () => {
    setSmsEditing(false);
    setSmsPhoneInput("");
    setSmsConsent(false);
  };

  const saveSmsPhone = async () => {
    if (!smsPhoneInput.trim()) {
      toast.error("Enter a phone number");
      return;
    }
    if (!smsConsent) {
      toast.error("Please check the consent box to receive texts");
      return;
    }
    setSmsSaving(true);
    try {
      const res = await axiosInstance.post("/sms/phone", {
        phoneNumber: smsPhoneInput,
        smsConsent: true,
        consentLanguage: SMS_CONSENT_LANGUAGE,
      });
      setSmsPhoneNumber(res.data.phoneNumber);
      // Server clears the prior opt-out when a new number is saved with
      // consent, so reflect that here too.
      setSmsOptedOut(false);
      toast.success("Phone saved — we'll text you when clips are ready");
      cancelSmsEditor();
    } catch (err) {
      toast.error(err.response?.data?.error || "Couldn't save phone");
    } finally {
      setSmsSaving(false);
    }
  };

  const removeSmsPhone = async () => {
    if (!window.confirm("Remove your phone number? You'll stop receiving SMS alerts.")) {
      return;
    }
    setSmsSaving(true);
    try {
      await axiosInstance.delete("/sms/phone");
      setSmsPhoneNumber(null);
      setSmsOptedOut(false);
      toast.success("Phone number removed");
      cancelSmsEditor();
    } catch (err) {
      toast.error(err.response?.data?.error || "Couldn't remove phone");
    } finally {
      setSmsSaving(false);
    }
  };

  // Refetch when navigating to this page or search params cnge
  useEffect(() => {
    fetchCreatorSettings();
    fetchBrands();
    fetchCaptionPresets();
    fetchSmsPreferences();

    const params = new URLSearchParams(location.search);
    const connected = params.get("connected");
    if (connected) {
      toast.success(t('common:connectedTo', { platform: connected }));
      navigate("/me", { replace: true });
      fetchCreatorSettings();
      fetchBrands();
    }
    // Connect callbacks send failures back as ?error=<platform>_<reason>.
    const connectError = params.get("error");
    if (connectError) {
      const platform = ["tiktok", "twitter", "youtube", "facebook", "instagram", "x"].find((p) => connectError.startsWith(p));
      toast.error(platform ? `Couldn't connect ${platform === "x" || platform === "twitter" ? "X" : platform[0].toUpperCase() + platform.slice(1)}. Try again.` : "Couldn't connect that account. Try again.");
      navigate("/me", { replace: true });
    }
    if (params.get("newBrand") === "true") {
      setShowNewBrand(true);
      navigate("/me", { replace: true });
    }
  }, [location.search, location.key, navigate]);

  // Listen for manual refetch events (from bottom bar tap)
  useEffect(() => {
    const handleRefetch = () => {
      fetchCreatorSettings();
      fetchBrands();
      fetchCaptionPresets();
    };
    window.addEventListener("refetch_me", handleRefetch);
    return () => window.removeEventListener("refetch_me", handleRefetch);
  }, []);

  // Close brand 3-dot menu on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (brandMenuRef.current && !brandMenuRef.current.contains(e.target)) {
        setShowBrandMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axiosInstance.put("/users/me/creator-settings", {
        channelUsername,
        platform,
        streamerName,
        streamType,
      });
      toast.success(t('common:saved'));
      fetchBrands();
    } catch (err) {
      toast.error(t('common:failedToSave'));
    } finally {
      setSaving(false);
    }
  };

  const handleConnect = (platformKey) => {
    const token = localStorage.getItem("token");
    const path = platformKey === "x" ? "/auth/x" : `/auth/social/${platformKey}`;
    const url = `${axiosInstance.defaults.baseURL}${path}${token ? `?token=${token}` : ""}`;
    window.location.href = url;
  };

  const handleDisconnect = async (platformKey) => {
    try {
      await axiosInstance.delete(`/users/me/social/${platformKey}`);
      setSocialConnections(prev => ({
        ...prev,
        [platformKey]: { connected: false }
      }));
      toast.success(t('disconnectedFrom', { platform: platformKey }));
      fetchBrands();
    } catch (err) {
      toast.error(t('failedToDisconnect'));
    }
  };

  // Brand management
  const handleCreateBrand = async () => {
    if (!newBrandName.trim()) return;
    setCreatingBrand(true);
    try {
      const res = await axiosInstance.post("/users/me/brands", { name: newBrandName.trim() });
      const newBrand = { _id: res.data._id, name: newBrandName.trim(), avatar: null };
      setBrands(prev => [...prev, newBrand]);
      setWizardBrandId(res.data._id);
      setCreatingBrand(false);
      setWizardStep(1); // move to photo step
    } catch (err) {
      toast.error(err.response?.data?.error || t('failedToCreateBrand'));
      setCreatingBrand(false);
    }
  };


  // Sent here from the campaign launch flow: go back there when done.
  const leaveWizardTo = () => {
    if (!sessionStorage.getItem(ADD_STREAMER_RETURN_KEY)) return "/me";
    sessionStorage.removeItem(ADD_STREAMER_RETURN_KEY);
    return "/campaigns/launch";
  };

  const handleWizardFinish = async () => {
    if (!wizardBrandId) return;
    setCreatingBrand(true);
    try {
      await axiosInstance.put(`/users/me/brands/${wizardBrandId}`, {
        platform: wizardPlatform,
        channelUsername: wizardUsername,
        streamType: wizardContentType,
      });
      await axiosInstance.put(`/users/me/brands/${wizardBrandId}/activate`);
      toast.success(t('brandCreated'));
      window.location.replace(leaveWizardTo());
    } catch (err) {
      toast.error(t('failedToCreateBrand'));
      setCreatingBrand(false);
    }
  };

  const resetWizard = () => {
    if (sessionStorage.getItem(ADD_STREAMER_RETURN_KEY)) {
      window.location.replace(leaveWizardTo());
      return;
    }
    setShowNewBrand(false);
    setNewBrandName("");
    setWizardStep(0);
    setWizardBrandId(null);
    setWizardPlatform("");
    setWizardUsername("");
    setWizardContentType("");
  };

  const handleDeleteBrand = async () => {
    if (brands.length <= 1) {
      toast.error(t('cannotDeleteLastBrand'));
      return;
    }
    setDeletingBrand(true);
    try {
      await axiosInstance.delete(`/users/me/brands/${activeBrandId}`);
      toast.success(t('brandDeleted'));
      window.location.replace("/me");
    } catch (err) {
      toast.error(err.response?.data?.error || t('failedToDeleteBrand'));
      setDeletingBrand(false);
      setShowDeleteBrandConfirm(false);
    }
  };

  // Account settings
  // Same tab: a new tab opened after the await is silently blocked by Safari
  // and in-app browsers, which looked like the button doing nothing.
  const [portalOpening, setPortalOpening] = useState(false);
  const handleManageSubscription = async () => {
    if (portalOpening) return;
    setPortalOpening(true);
    try {
      const res = await axiosInstance.post("/billing/create-portal-session", {
        return_url: `${window.location.origin}/me`,
      });
      if (res.data?.url) { window.location.href = res.data.url; return; }
      toast.error(t('failedToOpenBilling'));
    } catch (err) {
      toast.error(t('failedToOpenBilling'));
    }
    setPortalOpening(false);
  };

  const handleDeleteAccount = async () => {
    if (deleteInput !== "DELETE") return;
    setDeleting(true);
    try {
      await axiosInstance.delete(`/users/${currentUser._id}`, {
        data: { userId: currentUser._id },
      });
      dispatch({ type: "LOGOUT" });
      localStorage.removeItem("clipbait_token");
      localStorage.removeItem("token");
      toast.success(t('accountDeleted'));
      navigate("/");
    } catch (err) {
      toast.error(t('failedToDeleteAccount'));
    } finally {
      setDeleting(false);
    }
  };

  const activeBrand = brands.find((b) => b._id === activeBrandId);

  return (
    <div className="me-page">
      <Topbar onUpgrade={() => setShowUpgradeModal(true)} />

      {/* Desk Left Sidebar */}
      <div className="me-leftbar-wrapper">
        <Leftbar />
      </div>

      <div className="me-content">
        {/* Upgrade banner — top of profile for unpaid users. Loaded plan only
            (mePlan !== null) so it doesn't flash before we know their status. */}
        {mePlan === "none" && !meHasSub && (
          <button className="me-upgrade-banner" onClick={startTrial} disabled={trialOpening}>
            <div className="me-upgrade-banner-left">
              <span className="me-upgrade-banner-spark">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M13 2 3 14h7l-1 8 10-12h-7l1-8z" /></svg>
              </span>
              <div className="me-upgrade-banner-text">
                <strong>Start clipping for $1</strong>
              </div>
            </div>
            <span className="me-upgrade-banner-cta">Upgrade →</span>
          </button>
        )}

        {/* Paywall: Add Brand (and other premium features) are gated. Unpaid
            unsubscribed users get the pricing popup instead of the wizard. A
            free account gets ONE brand; adding more requires a subscription
            (credits don't unlock it). Waits for the plan to load (mePlan !==
            null) to avoid flashing the wizard. */}
        <UpgradeModal
          isOpen={
            showUpgradeModal ||
            (showNewBrand && mePlan !== null && mePlan === "none")
          }
          onClose={() => {
            setShowUpgradeModal(false);
            setShowNewBrand(false);
          }}
          message="Subscribe to add more streamers."
          currentPlan={mePlan}
        />


        {/* Add Streamer — same look and feel as onboarding (onboarding.css). Subscribers only. */}
        {showNewBrand && mePlan !== null && mePlan !== "none" && (() => {
          const currentAvatar = brands.find(b => b._id === wizardBrandId)?.avatar || null;
          const who = newBrandName.trim() || "them";
          const back = () => (wizardStep <= 1 ? resetWizard() : setWizardStep(wizardStep - 1));
          return (
            <div className="ob">
              <div className="ob-top">
                <button type="button" className="ob-back" onClick={back} aria-label="Back"><FiArrowLeft /></button>
                <div className="ob-progress"><span style={{ width: `${((wizardStep + 1) / 5) * 100}%` }} /></div>
              </div>
              <div key={wizardStep} className="ob-screen ob-in-right">

                {wizardStep === 0 && (
                  <>
                    <h1 className="ob-title">Which streamer are you adding?</h1>
                    <p className="ob-sub">Their name helps the AI write better hooks and titles for their clips.</p>
                    <div className="ob-body">
                      <input className="ob-input" autoFocus name="cb-streamer-name" autoComplete="off" autoCorrect="off" autoCapitalize="words" spellCheck={false}
                        data-1p-ignore="true" data-lpignore="true" data-form-type="other"
                        placeholder="e.g. Kai Cenat" value={newBrandName}
                        onChange={(e) => setNewBrandName(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && newBrandName.trim() && handleCreateBrand()} />
                      <div className="ob-footer">
                        <button type="button" className="ob-cta" onClick={handleCreateBrand} disabled={creatingBrand || !newBrandName.trim()}>
                          {creatingBrand ? "Adding…" : "Continue"}
                        </button>
                      </div>
                    </div>
                  </>
                )}

                {wizardStep === 1 && (
                  <>
                    <h1 className="ob-title ob-title--white">Clipbait needs to recognize {who}</h1>
                    <p className="ob-why">The AI uses their face to pick them out on stream, so clips follow the right person. <b>No photo = worse clips.</b></p>
                    <div className={`ob-face${draggingPhoto ? " ob-face--drop" : ""}`}>
                      <div className="ob-face-frame">
                        {currentAvatar ? <img src={currentAvatar} alt="" /> : <span className="ob-face-empty"><FiUser /></span>}
                        <span className="ob-face-box" />
                      </div>
                    </div>
                    <label className="ob-cta ob-cta--secondary" style={{ cursor: "pointer" }}>
                      <input type="file" accept="image/*" hidden onChange={(e) => handlePictureChange(e, wizardBrandId)} />
                      <FiCamera /> {uploadingPicture ? "Uploading…" : draggingPhoto ? "Drop the photo" : currentAvatar ? "Change photo" : "Upload a photo"}
                    </label>
                    {currentAvatar && (
                      <div className="ob-footer">
                        <button type="button" className="ob-cta" onClick={() => setWizardStep(2)} disabled={uploadingPicture}>Continue</button>
                      </div>
                    )}
                  </>
                )}

                {wizardStep === 2 && (
                  <>
                    <h1 className="ob-title">Where does {who} stream?</h1>
                    <div className="ob-body">
                      {PLATFORMS.filter(p => p.value).map(p => (
                        <button key={p.value} type="button" className={`ob-opt${wizardPlatform === p.value ? " ob-opt--on" : ""}`}
                          onClick={() => { setWizardPlatform(p.value); setWizardStep(3); }}>
                          {PLATFORM_ICON[p.value] && <img src={PLATFORM_ICON[p.value]} alt="" className="ob-opt-icon" />}
                          <span className="ob-opt-text"><span className="ob-opt-label">{t(p.labelKey)}</span></span>
                        </button>
                      ))}
                    </div>
                  </>
                )}

                {wizardStep === 3 && (
                  <>
                    <h1 className="ob-title">What&apos;s their channel handle?</h1>
                    <p className="ob-sub">The name in their channel link. No @ symbol.</p>
                    <div className="ob-body">
                      <input className="ob-input" autoFocus name="cb-channel-handle" autoComplete="off" autoCorrect="off" autoCapitalize="none" spellCheck={false}
                        data-1p-ignore="true" data-lpignore="true" data-form-type="other"
                        placeholder="kaicenat" value={wizardUsername}
                        onChange={(e) => setWizardUsername(e.target.value.replace(/[@\s]/g, ""))}
                        onKeyDown={(e) => e.key === "Enter" && wizardUsername.trim() && setWizardStep(4)} />
                      <div className="ob-footer">
                        <button type="button" className="ob-cta" onClick={() => setWizardStep(4)} disabled={!wizardUsername.trim()}>Continue</button>
                      </div>
                    </div>
                  </>
                )}

                {wizardStep === 4 && (
                  <>
                    <h1 className="ob-title">What does {who} stream?</h1>
                    <p className="ob-sub">This helps the AI pick the best clips.</p>
                    <div className="ob-body">
                      {STREAM_TYPES.map(st => (
                        <button key={st.value} type="button" className={`ob-opt${wizardContentType === st.value ? " ob-opt--on" : ""}`}
                          onClick={() => setWizardContentType(st.value)}>
                          <span className="ob-opt-glyph">{STREAM_TYPE_ICONS[st.value]}</span>
                          <span className="ob-opt-text">
                            <span className="ob-opt-label">{t(st.labelKey)}</span>
                            <span className="ob-opt-sub">{t(st.descKey)}</span>
                          </span>
                        </button>
                      ))}
                      <div className="ob-footer">
                        <button type="button" className="ob-cta" onClick={handleWizardFinish} disabled={creatingBrand || !wizardContentType}>
                          {creatingBrand ? "Adding…" : "Add streamer"}
                        </button>
                      </div>
                    </div>
                  </>
                )}

              </div>
            </div>
          );
        })()}

        {!showNewBrand && <>
        {/* Profile Picture + 3-dot menu */}
        <div className="me-section me-profile-section">
          <div className="me-profile-picture-container">
            <div
              className={`me-profile-picture ${uploadingPicture ? "uploading" : ""}`}
              onClick={handlePictureClick}
            >
              {getProfilePictureUrl() ? (
                <Avatar
                  src={getProfilePictureUrl()}
                  name={streamerName || currentUser?.username || "?"}
                />
              ) : (
                <div className="me-profile-picture-placeholder">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M20 21a8 8 0 10-16 0" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                </div>
              )}
              <div className="me-profile-picture-overlay">
                {uploadingPicture ? (
                  <span className="me-profile-picture-spinner" />
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                    <circle cx="12" cy="13" r="4" />
                  </svg>
                )}
              </div>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handlePictureChange}
              style={{ display: "none" }}
            />
            <div className="me-profile-name-row">
              <span className="me-profile-picture-name">
                {activeBrand?.name || channelUsername ? `@${channelUsername}` : <span className="me-profile-picture-spinner-inline" />}
              </span>
              {brands.length > 1 && (
                <div className="me-brand-menu-wrapper" ref={brandMenuRef}>
                  <button
                    className="me-brand-menu-btn"
                    onClick={() => setShowBrandMenu((v) => !v)}
                  >
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                      <circle cx="12" cy="5" r="2" />
                      <circle cx="12" cy="12" r="2" />
                      <circle cx="12" cy="19" r="2" />
                    </svg>
                  </button>
                  {showBrandMenu && (
                    <div className="me-brand-menu-dropdown">
                      <button
                        className="me-brand-menu-item me-brand-menu-delete"
                        onClick={() => { setShowBrandMenu(false); setShowDeleteBrandConfirm(true); }}
                      >
                        {t('deleteBrand')}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
            <span className="me-profile-picture-hint">{t('watermarkDescription')}</span>
          </div>
        </div>

        {/* Connected Accounts */}
        <div className="me-section">
          <h3 className="me-section-title">
            {t('connectedAccounts')}
            {activeBrand && <span className="me-section-brand-tag">{activeBrand.name}</span>}
          </h3>
          <p className="me-section-desc">
            {t('linkSocialsToPost')}
          </p>

          <div className="me-social-list">
            {(isAdmin ? [...SOCIAL_PLATFORMS, ...ADMIN_ONLY_PLATFORMS] : SOCIAL_PLATFORMS).map((sp) => {
              const connection = socialConnections[sp.key];
              const isConnected = connection?.connected;

              return (
                <div key={sp.key} className="me-social-item">
                  <div className="me-social-info">
                    <span
                      className="me-social-icon"
                      style={{ backgroundColor: sp.color }}
                    >
                      {sp.iconImg ? <img src={sp.iconImg} alt="" style={{ width: 18, height: 18 }} /> : sp.icon}
                    </span>
                    <div className="me-social-details">
                      <span className="me-social-name">{t(sp.nameKey)}</span>
                      {isConnected && (
                        <span className="me-social-username">
                          @{connection.username || connection.channelName || connection.pageName}
                        </span>
                      )}
                      {isConnected && sp.key === "tiktok" && (
                        <button type="button" className="me-social-analytics" onClick={() => navigate("/analytics")}>
                          View analytics
                        </button>
                      )}
                    </div>
                  </div>

                  {sp.comingSoon ? (
                    <span className="me-social-coming-soon">{t('common:comingSoon')}</span>
                  ) : isConnected ? (
                    <button
                      className="me-social-btn me-social-disconnect"
                      onClick={() => handleDisconnect(sp.key)}
                    >
                      {t('common:disconnect')}
                    </button>
                  ) : (
                    <button
                      className="me-social-btn me-social-connect"
                      onClick={() => handleConnect(sp.key)}
                    >
                      {t('common:connect')}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Creator Settings */}
        <div className="me-section">
          <h3 className="me-section-title">{t('yourChannel')}</h3>

          <div className="me-field">
            <label className="me-label">{t('streamerName')}</label>
            <input
              type="text"
              className="me-input"
              placeholder={t('placeholderStreamerName')}
              value={streamerName}
              onChange={(e) => setStreamerName(e.target.value)}
            />
            <span className="me-field-hint">{t('streamerNameHint')}</span>
          </div>

          <div className="me-field">
            <label className="me-label">{t('channelUsername')}</label>
            <input
              type="text"
              className="me-input"
              placeholder={t('placeholderChannelUsername')}
              value={channelUsername}
              onChange={(e) => setChannelUsername(e.target.value)}
            />
          </div>

          <div className="me-field">
            <label className="me-label">{t('platform')}</label>
            <select
              className="me-select"
              value={platform}
              onChange={(e) => setPlatform(e.target.value)}
            >
              {PLATFORMS.map((p) => (
                <option key={p.value} value={p.value}>
                  {t(p.labelKey)}
                </option>
              ))}
            </select>
          </div>

          <div className="me-field">
            <label className="me-label">{t('streamType')}</label>
            <p className="me-field-hint" style={{ marginBottom: 8 }}>{t('streamTypeHint')}</p>
            <div className="me-stream-type-grid">
              {STREAM_TYPES.map((st) => (
                <button
                  key={st.value}
                  className={`me-stream-type-card${streamType === st.value ? " active" : ""}`}
                  onClick={() => setStreamType(st.value)}
                  type="button"
                >
                  <span className="me-stream-type-icon">{STREAM_TYPE_ICONS[st.value]}</span>
                  <span className="me-stream-type-label">{t(st.labelKey)}</span>
                </button>
              ))}
            </div>
          </div>

          <button
            className="me-save-btn"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? t('common:saving') : t('common:save')}
          </button>
        </div>

        {/* Caption Style Presets — hidden; backend now uses a fixed default
            from MongoDB instead of per-user presets. To re-enable, change
            the false below to true. */}
        {false && (
        <div className="me-section">
          <div className="me-presets-header">
            <div>
              <h3 className="me-section-title">{t('captionPresets')}</h3>
              <p className="me-section-desc" style={{ marginBottom: 0 }}>
                {t('captionPresetsDesc')}
              </p>
            </div>
            <button className="me-presets-add-btn" onClick={handleNewPreset}>
              + {t('addPreset')}
            </button>
          </div>

          <div className="me-presets-list">
            {captionPresets.map((preset) => {
              const isActive = preset._id === activePresetId;
              const colorPreset = HOOK_COLORS.find((c) => c.id === preset.hookColor) || HOOK_COLORS[0];
              const fontPreset = HOOK_FONTS.find((f) => f.id === preset.hookFont) || HOOK_FONTS[0];
              return (
                <div
                  key={preset._id}
                  className={`me-preset-card ${isActive ? "active" : ""}`}
                  onClick={() => handleSwitchPreset(preset._id)}
                >
                  <div className="me-preset-card-left">
                    <span className="me-brand-radio">
                      {isActive ? "●" : "○"}
                    </span>
                    <div className="me-preset-card-info">
                      <span className="me-preset-card-name">{preset.name}</span>
                      <div className="me-preset-indicator">
                        <span
                          className="me-preset-color-dot"
                          style={{ background: colorPreset.dot, borderColor: colorPreset.border }}
                        />
                        <span className="me-preset-font-label">{fontPreset.label}</span>
                      </div>
                    </div>
                  </div>
                  <div className="me-preset-card-actions">
                    <button
                      className="me-preset-edit-btn"
                      onClick={(e) => { e.stopPropagation(); handleEditPreset(preset); }}
                      title={t('editPreset')}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                    </button>
                    {captionPresets.length > 1 && (
                      <button
                        className="me-brand-delete-btn"
                        onClick={(e) => { e.stopPropagation(); handleDeletePreset(preset._id); }}
                        title={t('deletePreset')}
                      >
                        ×
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Preset Editor Panel */}
          {showPresetEditor && (() => {
            const formColor = HOOK_COLORS.find((c) => c.id === presetForm.hookColor) || HOOK_COLORS[0];
            const formFont = HOOK_FONTS.find((f) => f.id === presetForm.hookFont) || HOOK_FONTS[0];
            return (
              <div className="me-preset-editor">
                {/* Name */}
                <div className="me-preset-editor-row">
                  <label className="me-label">{t('presetName')}</label>
                  <input
                    type="text"
                    className="me-input"
                    placeholder={t('presetNamePlaceholder')}
                    value={presetForm.name}
                    onChange={(e) => setPresetForm((f) => ({ ...f, name: e.target.value }))}
                    autoFocus
                  />
                </div>

                {/* Live Preview */}
                <div className="me-preset-preview">
                  <div
                    className="me-preset-preview-caption"
                    style={{
                      top: `${presetForm.hookY}%`,
                      width: `${presetForm.hookWidth}%`,
                      background: formColor.bg,
                      color: formColor.text,
                      fontFamily: formFont.family,
                    }}
                  >
                    {t('captionPreview')}
                  </div>
                </div>

                {/* Color */}
                <div className="me-preset-editor-row">
                  <label className="me-label">{t('captionColor')}</label>
                  <div className="me-preset-color-picker">
                    {HOOK_COLORS.map((c) => (
                      <button
                        key={c.id}
                        className={`me-preset-color-btn ${presetForm.hookColor === c.id ? "active" : ""}`}
                        style={{ background: c.dot, borderColor: c.border }}
                        onClick={() => setPresetForm((f) => ({ ...f, hookColor: c.id }))}
                        title={c.id}
                      />
                    ))}
                  </div>
                </div>

                {/* Font */}
                <div className="me-preset-editor-row">
                  <label className="me-label">{t('captionFont')}</label>
                  <div className="me-preset-font-picker">
                    {HOOK_FONTS.map((f) => (
                      <button
                        key={f.id}
                        className={`me-preset-font-btn ${presetForm.hookFont === f.id ? "active" : ""}`}
                        style={{ fontFamily: f.family }}
                        onClick={() => setPresetForm((form) => ({ ...form, hookFont: f.id }))}
                      >
                        {f.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Position */}
                <div className="me-preset-editor-row">
                  <label className="me-label">{t('captionPosition')}</label>
                  <div className="me-preset-slider-row">
                    <input
                      type="range"
                      min="0"
                      max="90"
                      value={presetForm.hookY}
                      onChange={(e) => setPresetForm((f) => ({ ...f, hookY: Number(e.target.value) }))}
                    />
                    <span className="me-preset-slider-value">{presetForm.hookY}%</span>
                  </div>
                </div>

                {/* Width */}
                <div className="me-preset-editor-row">
                  <label className="me-label">{t('captionWidth')}</label>
                  <div className="me-preset-slider-row">
                    <input
                      type="range"
                      min="50"
                      max="100"
                      value={presetForm.hookWidth}
                      onChange={(e) => setPresetForm((f) => ({ ...f, hookWidth: Number(e.target.value) }))}
                    />
                    <span className="me-preset-slider-value">{presetForm.hookWidth}%</span>
                  </div>
                </div>

                {/* Save / Cancel */}
                <div className="me-preset-editor-actions">
                  <button
                    className="me-save-btn"
                    onClick={handleSavePreset}
                    disabled={savingPreset || !presetForm.name.trim()}
                  >
                    {savingPreset ? t('common:saving') : editingPreset ? t('common:save') : t('createPreset')}
                  </button>
                  <button
                    className="me-cancel-btn"
                    onClick={() => { setShowPresetEditor(false); setEditingPreset(null); }}
                  >
                    {t('common:cancel')}
                  </button>
                </div>
              </div>
            );
          })()}
        </div>
        )}

        {/* Developers / API — agent access (MCP, CLI, direct API) */}
        <DeveloperApiSection />

        {/* Account Settings */}
        <div className="me-section">
          <h3 className="me-section-title">{t('accountSettings')}</h3>

          <div className="me-account-actions">
            <div className="me-account-btn me-sms-card">
              <div className="me-account-btn-info">
                <span className="me-account-btn-title">SMS notifications</span>
                <span className="me-account-btn-desc">
                  Get a text when your clips are ready, plus the occasional
                  feature update or offer from Clipbait. Reply STOP any time
                  to opt out.
                </span>
                {smsPhoneNumber && !smsEditing && (
                  <span className="me-sms-current">
                    Texts go to <strong>{smsPhoneNumber}</strong>
                  </span>
                )}
              </div>

              {smsPhoneNumber && !smsEditing && (
                <button
                  type="button"
                  role="switch"
                  aria-checked={smsOptedOut === false}
                  aria-label="Toggle SMS notifications"
                  className={`me-toggle-switch${smsOptedOut === false ? " me-toggle-switch--on" : ""}`}
                  onClick={handleToggleSmsNotifications}
                  disabled={smsUpdating || smsOptedOut === null}
                >
                  <span className="me-toggle-switch-thumb" />
                </button>
              )}
            </div>

            {!smsEditing && (
              <div className="me-sms-buttons">
                <button
                  type="button"
                  className="me-sms-edit-btn"
                  onClick={openSmsEditor}
                >
                  {smsPhoneNumber ? "Change number" : "Add phone number"}
                </button>
                {smsPhoneNumber && (
                  <button
                    type="button"
                    className="me-sms-remove-btn"
                    onClick={removeSmsPhone}
                    disabled={smsSaving}
                  >
                    Remove number
                  </button>
                )}
              </div>
            )}

            {smsEditing && (
              <div className="me-sms-editor">
                <label className="me-sms-editor-label" htmlFor="me-sms-phone-input">
                  Mobile number
                </label>
                <input
                  id="me-sms-phone-input"
                  type="tel"
                  className="me-sms-editor-input"
                  placeholder="+1 555 867 5309"
                  value={smsPhoneInput}
                  onChange={(e) => setSmsPhoneInput(e.target.value)}
                  onKeyDown={(e) =>
                    e.key === "Enter" && !smsSaving && smsConsent && saveSmsPhone()
                  }
                  autoFocus
                />

                {/* Explicit, optional SMS consent — TCPA / A2P 10DLC compliant.
                    Unchecked by default on every open (including when only
                    changing an existing number). Stands alone — not bundled
                    with any other consent. */}
                <label className="me-sms-consent">
                  <input
                    type="checkbox"
                    className="me-sms-consent-box"
                    checked={smsConsent}
                    onChange={(e) => setSmsConsent(e.target.checked)}
                  />
                  <span className="me-sms-consent-text">
                    {SMS_CONSENT_LANGUAGE}
                  </span>
                </label>

                <div className="me-sms-editor-actions">
                  <button
                    type="button"
                    className="me-sms-save-btn"
                    onClick={saveSmsPhone}
                    disabled={smsSaving || !smsPhoneInput.trim() || !smsConsent}
                  >
                    {smsSaving ? "Saving…" : "Save number"}
                  </button>
                  <button
                    type="button"
                    className="me-sms-cancel-btn"
                    onClick={cancelSmsEditor}
                    disabled={smsSaving}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            <button
              className="me-account-btn me-account-manage"
              onClick={handleManageSubscription}
              disabled={portalOpening}
            >
              <div className="me-account-btn-info">
                <span className="me-account-btn-title">{t('manageSubscription')}</span>
              </div>
              <span className="me-account-btn-arrow">→</span>
            </button>

            <button
              className="me-account-btn me-account-delete"
              onClick={() => setShowDeleteConfirm(true)}
            >
              <div className="me-account-btn-info">
                <span className="me-account-btn-title">{t('deleteAccount')}</span>
              </div>
            </button>
          </div>

          {/* Delete Brand Confirmation Modal */}
          {showDeleteBrandConfirm && (
            <div className="me-delete-modal-overlay" onClick={() => setShowDeleteBrandConfirm(false)}>
              <div className="me-delete-modal" onClick={(e) => e.stopPropagation()}>
                <h4 className="me-delete-modal-title">{t('deleteBrand')}</h4>
                <p className="me-delete-modal-text">
                  {t('deleteBrandWarning', { brand: activeBrand?.name || '' })}
                </p>
                <div className="me-delete-modal-actions">
                  <button
                    className="me-cancel-btn"
                    onClick={() => setShowDeleteBrandConfirm(false)}
                  >
                    {t('common:cancel')}
                  </button>
                  <button
                    className="me-delete-confirm-btn"
                    onClick={handleDeleteBrand}
                    disabled={deletingBrand}
                  >
                    {deletingBrand ? t('common:deleting') : t('deleteBrand')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Delete Account Confirmation Modal */}
          {showDeleteConfirm && (
            <div className="me-delete-modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
              <div className="me-delete-modal" onClick={(e) => e.stopPropagation()}>
                <h4 className="me-delete-modal-title">{t('deleteAccount')}</h4>
                <p className="me-delete-modal-text">{t('deleteAccountWarning')}</p>
                <p className="me-delete-modal-confirm">{t('typeDeleteToConfirm')}</p>
                <input
                  type="text"
                  className="me-input"
                  value={deleteInput}
                  onChange={(e) => setDeleteInput(e.target.value)}
                  placeholder="DELETE"
                  autoFocus
                />
                <div className="me-delete-modal-actions">
                  <button
                    className="me-cancel-btn"
                    onClick={() => {
                      setShowDeleteConfirm(false);
                      setDeleteInput("");
                    }}
                  >
                    {t('common:cancel')}
                  </button>
                  <button
                    className="me-delete-confirm-btn"
                    onClick={handleDeleteAccount}
                    disabled={deleteInput !== "DELETE" || deleting}
                  >
                    {deleting ? t('common:deleting') : t('deleteAccount')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
        </>}
      </div>
    </div>
  );
}
